import { useState, useEffect, useMemo } from 'react';
import { OnboardingState } from '../OnboardingWizard';
import { Building2, Search, CheckCircle } from 'lucide-react';
import { getClients } from '../../../services/api';
import { Client } from '../../../types';

interface Props {
  state: OnboardingState;
  updateState: (updates: Partial<OnboardingState>) => void;
  onNext: () => void;
  setError: (error: string | null) => void;
}

export default function BasicInfoStep({ state, updateState, onNext, setError }: Props) {
  const [mode, setMode] = useState<'new' | 'existing'>(state.isExistingClient ? 'existing' : 'new');
  const [companyName, setCompanyName] = useState(state.companyName);
  const [primaryContact, setPrimaryContact] = useState(state.primaryContact);
  const [email, setEmail] = useState(state.email);

  // For existing client selection
  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  // Load clients when existing mode is selected
  useEffect(() => {
    if (mode === 'existing' && clients.length === 0) {
      loadClients();
    }
  }, [mode]);

  async function loadClients() {
    setLoadingClients(true);
    try {
      const response = await getClients({ pageSize: 500 });
      setClients(response.items);
    } catch (err) {
      setError('Failed to load clients');
    } finally {
      setLoadingClients(false);
    }
  }

  // Filter to only clients that need GDAP upgrade:
  // - Has tenantId (linked to MS tenant, required for GDAP)
  // - Has pax8CompanyId (came from Pax8, likely has Pax8's limited GDAP)
  const clientsNeedingUpgrade = useMemo(() => {
    return clients.filter(c => c.tenantId && c.pax8CompanyId);
  }, [clients]);

  // Filter by search
  const filteredClients = useMemo(() => {
    if (!clientSearch.trim()) return clientsNeedingUpgrade;
    const search = clientSearch.toLowerCase();
    return clientsNeedingUpgrade.filter(c =>
      c.companyName.toLowerCase().includes(search) ||
      c.contacts?.some(contact =>
        contact.name?.toLowerCase().includes(search) ||
        contact.email?.toLowerCase().includes(search)
      )
    );
  }, [clientsNeedingUpgrade, clientSearch]);

  // Handle selecting an existing client
  function handleSelectClient(client: Client) {
    setSelectedClient(client);
    setCompanyName(client.companyName);
    // Use first contact if available
    const primaryContact = client.contacts?.[0];
    setPrimaryContact(primaryContact?.name || '');
    setEmail(primaryContact?.email || '');
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!companyName.trim()) {
      setError('Company name is required');
      return;
    }

    if (mode === 'existing' && !selectedClient) {
      setError('Please select an existing client');
      return;
    }

    // Update state and proceed
    updateState({
      companyName: companyName.trim(),
      primaryContact: primaryContact.trim(),
      email: email.trim(),
      isExistingClient: mode === 'existing',
      existingClientId: selectedClient?.id || null,
      existingTenantId: selectedClient?.tenantId || null,
      existingTenantDomain: selectedClient?.tenantDomain || null,
      // If existing client, pre-populate clientId so later steps can update it
      clientId: selectedClient?.id || null,
    });
    onNext();
  };

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <Building2 className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              Client Information
            </h2>
            <p className="text-sm text-muted-foreground">
              Create new GDAP with all 26 admin roles
            </p>
          </div>
        </div>
      </div>

      {/* Mode Selection */}
      <div className="mb-6">
        <div className="flex space-x-4">
          <button
            type="button"
            onClick={() => {
              setMode('new');
              setSelectedClient(null);
              setCompanyName('');
              setPrimaryContact('');
              setEmail('');
            }}
            className={`flex-1 p-4 rounded-lg border-2 transition-all ${
              mode === 'new'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-border hover:border-border'
            }`}
          >
            <div className="text-left">
              <p className={`font-medium ${mode === 'new' ? 'text-blue-700 dark:text-blue-300' : 'text-foreground'}`}>
                New Client
              </p>
              <p className="text-sm text-muted-foreground">
                Client not in Pax8 yet
              </p>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setMode('existing')}
            className={`flex-1 p-4 rounded-lg border-2 transition-all ${
              mode === 'existing'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-border hover:border-border'
            }`}
          >
            <div className="text-left">
              <p className={`font-medium ${mode === 'existing' ? 'text-blue-700 dark:text-blue-300' : 'text-foreground'}`}>
                Existing Client
              </p>
              <p className="text-sm text-muted-foreground">
                Upgrade Pax8 GDAP to full access
              </p>
            </div>
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {mode === 'existing' && (
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg mb-4">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              <strong>Why upgrade GDAP?</strong> Pax8's GDAP only includes basic roles.
              This wizard creates a new GDAP with all 26 roles including Groups Administrator,
              which is needed to manage group memberships.
            </p>
          </div>
        )}

        {mode === 'existing' ? (
          <>
            {/* Client Search */}
            <div>
              <label className="label">
                Select Client <span className="text-red-500">*</span>
                {clientsNeedingUpgrade.length > 0 && (
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    ({clientsNeedingUpgrade.length} Pax8 client{clientsNeedingUpgrade.length !== 1 ? 's' : ''} need{clientsNeedingUpgrade.length === 1 ? 's' : ''} upgrade)
                  </span>
                )}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  className="input pl-10"
                  placeholder="Search clients..."
                  autoFocus
                />
              </div>

              {loadingClients ? (
                <div className="mt-2 p-4 text-center text-muted-foreground">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-sm">Loading clients...</p>
                </div>
              ) : (
                <div className="mt-2 max-h-60 overflow-y-auto border border-border rounded-lg divide-y divide-border">
                  {filteredClients.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground text-sm">
                      {clientSearch
                        ? 'No matching clients found'
                        : clientsNeedingUpgrade.length === 0
                          ? 'All Pax8 clients already have full GDAP or no tenant linked'
                          : 'No clients available'}
                    </div>
                  ) : (
                    filteredClients.map(client => {
                      const contact = client.contacts?.[0];
                      return (
                        <button
                          key={client.id}
                          type="button"
                          onClick={() => handleSelectClient(client)}
                          className={`w-full p-3 text-left hover:bg-accent transition-colors flex items-center justify-between ${
                            selectedClient?.id === client.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                          }`}
                        >
                          <div>
                            <p className="font-medium text-foreground">
                              {client.companyName}
                            </p>
                            {contact?.name && (
                              <p className="text-sm text-muted-foreground">
                                {contact.name}
                              </p>
                            )}
                            {client.tenantId && (
                              <p className="text-xs text-muted-foreground">
                                Tenant: {client.tenantId.substring(0, 8)}...
                              </p>
                            )}
                          </div>
                          {selectedClient?.id === client.id && (
                            <CheckCircle className="h-5 w-5 text-blue-600" />
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {selectedClient && (
              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
                <p className="text-sm font-medium text-green-800 dark:text-green-200">
                  Selected: {selectedClient.companyName}
                </p>
                {selectedClient.tenantId && (
                  <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                    Tenant ID will be pre-filled for GDAP request
                  </p>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            {/* New client fields */}
            <div>
              <label className="label">
                Company Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="input"
                placeholder="e.g., Acme Corporation"
                autoFocus
              />
              <p className="mt-1 text-sm text-muted-foreground">
                This will be used to name the GDAP relationship
              </p>
            </div>

            <div>
              <label className="label">Primary Contact</label>
              <input
                type="text"
                value={primaryContact}
                onChange={(e) => setPrimaryContact(e.target.value)}
                className="input"
                placeholder="e.g., John Smith"
              />
            </div>

            <div>
              <label className="label">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="e.g., john@acme.com"
              />
            </div>
          </>
        )}

        <div className="pt-4 border-t border-border">
          <button type="submit" className="btn-primary w-full sm:w-auto">
            Continue to GDAP Setup &rarr;
          </button>
        </div>
      </form>
    </div>
  );
}
